// hooks/useEffectiveUser.ts
// Combines AuthContext and ImpersonationContext to expose a unified "effective user"
// perspective. When an admin is impersonating a user, effectiveUserId is the
// impersonated user's ID and effectiveIsAdmin is false, so all data-fetching hooks
// and pages render the target user's view. All mutation-guarding logic reads canWrite.

import { useAuth } from '../contexts/AuthContext';
import { useImpersonation } from '../contexts/ImpersonationContext';

export interface EffectiveUser {
  /** The user ID to use for all data queries (impersonated or real). */
  effectiveUserId: string | null;
  /** False when impersonating — causes pages to render the user view, not admin view. */
  effectiveIsAdmin: boolean;
  isImpersonating: boolean;
  isWriteUnlocked: boolean;
  /** True when the current context allows performing write operations. */
  canWrite: boolean;
}

export function useEffectiveUser(): EffectiveUser {
  const { user, userProfile } = useAuth();
  const { isImpersonating, isWriteUnlocked, effectiveUserId } = useImpersonation();

  const realIsAdmin = userProfile?.isAdmin ?? false;

  return {
    effectiveUserId: effectiveUserId ?? user?.id ?? null,
    effectiveIsAdmin: isImpersonating ? false : realIsAdmin,
    isImpersonating,
    isWriteUnlocked,
    canWrite: !isImpersonating || isWriteUnlocked,
  };
}
